import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  ChatWebRTCSignalInput,
  ChatMessageAttachmentInput,
  clientPortalApi,
  realtimeApi,
} from '../../services/api';
import { useClientPortalAuth } from '../../context/ClientPortalAuthContext';
import { useToast } from '../../components/Toast';
import {
  ClientPortalChatConversation,
  ClientPortalChatUpdateSignal,
  ClientPortalNotificationSummary,
  SupportChatAttachment,
  SupportChatCallLog,
  SupportChatCallSignal,
  SupportChatMessage,
} from '../../types';
import './ClientPortal.css';

const MAX_CHAT_ATTACHMENTS = 5;
const MAX_CHAT_ATTACHMENT_BYTES = 8 * 1024 * 1024;
const CHAT_ATTACHMENT_EXTENSIONS = [
  '.pdf',
  '.png',
  '.jpg',
  '.jpeg',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.csv',
  '.txt',
  '.zip',
  '.rar',
];
const CHAT_ATTACHMENT_ACCEPT = CHAT_ATTACHMENT_EXTENSIONS.join(',');
const DEFAULT_WEBRTC_STUN_URL =
  process.env.REACT_APP_WEBRTC_STUN_URL || 'stun:stun.l.google.com:19302';
const DEFAULT_WEBRTC_TURN_URL = (process.env.REACT_APP_WEBRTC_TURN_URL || '').trim();
const DEFAULT_WEBRTC_TURN_USERNAME = (process.env.REACT_APP_WEBRTC_TURN_USERNAME || '').trim();
const DEFAULT_WEBRTC_TURN_CREDENTIAL = (process.env.REACT_APP_WEBRTC_TURN_CREDENTIAL || '').trim();
// Repli TLS (TURNS sur 5349) pour les réseaux qui bloquent l'UDP. Doit utiliser le domaine (le certificat ne correspond pas à l'IP).
const DEFAULT_WEBRTC_TURNS_URL = (process.env.REACT_APP_WEBRTC_TURNS_URL || '').trim();

type ClientPortalRealtimeEvent = {
  type?: string;
  request_id?: string;
  success?: boolean;
  error?: string;
  warning?: string;
  conversation_id?: number;
  client_id?: number;
  message_id?: number;
  created_at?: string;
  sender_type?: 'client' | 'staff';
  actor_name?: string;
  is_typing?: boolean;
  call_status?: 'idle' | 'ringing' | 'active';
  action?: string;
  initiator_type?: 'client' | 'staff';
  initiator_staff_id?: number | null;
  initiator_name?: string | null;
  assigned_staff_id?: number | null;
  assigned_staff_name?: string | null;
  reason?: string | null;
  updated_at?: string | null;
  support_online?: boolean;
  signal_type?: 'offer' | 'answer' | 'ice_candidate';
  actor_type?: 'client' | 'staff' | 'system';
  sdp?: string;
  candidate?: string;
  sdp_mid?: string | null;
  sdp_mline_index?: number | null;
};

type PendingSocketRequest = {
  resolve: (payload: ClientPortalRealtimeEvent) => void;
  reject: (error: Error) => void;
  timeoutId: number;
};

function isTransientSocketError(message: string): boolean {
  return (
    message === 'WEBSOCKET_UNAVAILABLE' ||
    message === 'WEBSOCKET_TIMEOUT' ||
    message === 'WEBSOCKET_CLOSED' ||
    message === 'WEBSOCKET_SEND_FAILED'
  );
}

function formatDateTime(value?: string | null): string {
  if (!value) {
    return '--';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatFileSize(size: number): string {
  if (size < 1024) {
    return `${size} o`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} Ko`;
  }

  return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
}

function formatDurationSeconds(seconds?: number): string {
  const totalSeconds = Number(seconds || 0);
  if (totalSeconds <= 0) {
    return '0 s';
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;
  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours} h`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} min`);
  }
  if (remainingSeconds > 0 || parts.length === 0) {
    parts.push(`${remainingSeconds} s`);
  }

  return parts.join(' ');
}

function getFileExtension(fileName: string): string {
  const lastDotIndex = fileName.lastIndexOf('.');
  return lastDotIndex >= 0 ? fileName.slice(lastDotIndex).toLowerCase() : '';
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const markerIndex = result.indexOf(',');
      if (markerIndex === -1) {
        reject(new Error('Encodage du fichier impossible.'));
        return;
      }

      resolve(result.slice(markerIndex + 1));
    };

    reader.onerror = () => reject(new Error(`Lecture du fichier "${file.name}" impossible.`));
    reader.readAsDataURL(file);
  });
}

function getMessageStatusLabel(message: SupportChatMessage): string {
  if (message.sender_type !== 'client') {
    return '';
  }

  return message.read_at ? 'Vu' : 'Envoye';
}

function getMessageStatusClassName(message: SupportChatMessage): string {
  return message.read_at ? 'seen' : 'sent';
}

function getLatestIsoTimestamp(values: Array<string | null | undefined>): string | null {
  let latestValue: string | null = null;
  let latestTime = 0;

  values.forEach((value) => {
    if (!value) {
      return;
    }

    const parsedTime = new Date(value).getTime();
    if (Number.isNaN(parsedTime)) {
      return;
    }

    if (!latestValue || parsedTime > latestTime) {
      latestValue = value;
      latestTime = parsedTime;
    }
  });

  return latestValue;
}

// Credentials TURN éphémères servis par notre backend (coturn du VPS,
// TTL 1 h). Rafraîchis à la connexion du chat puis à l'approche de
// l'expiration ; en cas d'échec, l'appel reste possible en STUN seul.
let dynamicIceServers: RTCIceServer[] | null = null;
let dynamicIceServersExpiresAt = 0;

async function refreshDynamicIceServers(): Promise<void> {
  try {
    const response = await clientPortalApi.getIceServers();
    const servers = response.data?.iceServers;
    const ttlSeconds = Number(response.data?.ttlSeconds || 0);
    if (Array.isArray(servers) && servers.length > 0) {
      dynamicIceServers = servers;
      // Marge de 5 min : ne jamais démarrer un appel avec des credentials
      // en fin de vie (l'allocation TURN doit survivre à l'appel entier).
      dynamicIceServersExpiresAt =
        Date.now() + Math.max(ttlSeconds - 300, 60) * 1000;
    }
  } catch {
    // Repli silencieux sur la configuration statique (STUN seul).
  }
}

function buildWebRTCIceServers(): RTCIceServer[] {
  if (dynamicIceServers && Date.now() < dynamicIceServersExpiresAt) {
    if (Date.now() > dynamicIceServersExpiresAt - 10 * 60 * 1000) {
      void refreshDynamicIceServers();
    }
    return dynamicIceServers;
  }
  void refreshDynamicIceServers();

  const iceServers: RTCIceServer[] = [];

  if (DEFAULT_WEBRTC_STUN_URL) {
    iceServers.push({ urls: [DEFAULT_WEBRTC_STUN_URL] });
  }

  if (DEFAULT_WEBRTC_TURN_URL || DEFAULT_WEBRTC_TURNS_URL) {
    const turnUrls: string[] = [];
    if (DEFAULT_WEBRTC_TURN_URL) turnUrls.push(DEFAULT_WEBRTC_TURN_URL);
    if (DEFAULT_WEBRTC_TURNS_URL) turnUrls.push(DEFAULT_WEBRTC_TURNS_URL);
    const turnServer: RTCIceServer = {
      urls: turnUrls,
    };
    if (DEFAULT_WEBRTC_TURN_USERNAME) {
      turnServer.username = DEFAULT_WEBRTC_TURN_USERNAME;
    }
    if (DEFAULT_WEBRTC_TURN_CREDENTIAL) {
      turnServer.credential = DEFAULT_WEBRTC_TURN_CREDENTIAL;
    }
    iceServers.push(turnServer);
  }

  return iceServers;
}

function getCallLogOutcomeLabel(callLog: SupportChatCallLog): string {
  switch ((callLog.outcome || '').toLowerCase()) {
    case 'completed':
      return 'Appel termine';
    case 'refused':
      return 'Appel refuse';
    case 'missed':
      return 'Appel manque';
    case 'busy':
      return 'Ligne occupee';
    case 'cancelled':
      return 'Appel annule';
    case 'staff_disconnected':
      return 'Support deconnecte';
    case 'client_disconnected':
      return 'Client deconnecte';
    default:
      return callLog.status === 'active' ? 'Appel en cours' : 'Historique appel';
  }
}

function getCallLogOutcomeClassName(callLog: SupportChatCallLog): string {
  switch ((callLog.outcome || '').toLowerCase()) {
    case 'completed':
      return 'completed';
    case 'busy':
    case 'missed':
      return 'warning';
    case 'refused':
    case 'cancelled':
    case 'staff_disconnected':
    case 'client_disconnected':
      return 'ended';
    default:
      return 'neutral';
  }
}

function getConversationStateToken(
  nextConversation: ClientPortalChatConversation | null
): string | null {
  if (!nextConversation) {
    return null;
  }

  const messageTimestamps = (nextConversation.messages || []).flatMap((message) => [
    message.updated_at,
    message.created_at,
    message.read_at,
  ]);
  const callLogTimestamps = (nextConversation.call_logs || []).flatMap((callLog) => [
    callLog.updated_at,
    callLog.requested_at,
    callLog.accepted_at,
    callLog.ended_at,
    callLog.created_at,
  ]);

  return getLatestIsoTimestamp([
    nextConversation.updated_at,
    nextConversation.last_message_at,
    nextConversation.created_at,
    nextConversation.closed_at,
    ...messageTimestamps,
    ...callLogTimestamps,
  ]);
}

function getChatStatusClassName(status?: string | null): string {
  return (status || '').toLowerCase() === 'cloture' ? 'closed' : 'open';
}

function isConversationClosed(status?: string | null): boolean {
  return (status || '').toLowerCase() === 'cloture';
}

function hasMessageText(message: SupportChatMessage): boolean {
  return Boolean(message.message_text && message.message_text.trim());
}

function getAudioSignalClassName(signal: SupportChatCallSignal): string {
  if (signal.call_status === 'active') {
    return 'active';
  }

  return signal.initiator_type === 'staff' ? 'incoming' : 'outgoing';
}

const ClientPortalChat: React.FC = () => {
  const { user } = useClientPortalAuth();
  const { showToast } = useToast();
  const [conversation, setConversation] = useState<ClientPortalChatConversation | null>(null);
  const [messageText, setMessageText] = useState('');
  const [chatAttachments, setChatAttachments] = useState<ChatMessageAttachmentInput[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [downloadingChatAttachmentId, setDownloadingChatAttachmentId] =
    useState<number | null>(null);
  const [supportOnline, setSupportOnline] = useState(false);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [remoteTypingLabel, setRemoteTypingLabel] = useState('');
  const [callSignal, setCallSignal] = useState<SupportChatCallSignal | null>(null);
  const [audioStatusLabel, setAudioStatusLabel] = useState('Aucun appel audio actif.');
  const [audioPreparing, setAudioPreparing] = useState(false);
  const [audioMuted, setAudioMuted] = useState(false);
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [videoPreparing, setVideoPreparing] = useState(false);
  const [hasRemoteVideo, setHasRemoteVideo] = useState(false);
  const [processingCallAction, setProcessingCallAction] = useState<
    'request' | 'accept' | 'refuse' | 'end' | null
  >(null);
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const threadEndRef = useRef<HTMLDivElement | null>(null);
  const conversationStateRef = useRef<string | null>(null);
  const supportOnlineRef = useRef(false);
  const socketRef = useRef<WebSocket | null>(null);
  const socketActiveRef = useRef(false);
  const conversationRef = useRef<ClientPortalChatConversation | null>(null);
  const callSignalRef = useRef<SupportChatCallSignal | null>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const pendingIceCandidatesRef = useRef<RTCIceCandidateInit[]>([]);
  const activeAudioConversationIdRef = useRef<number | null>(null);
  const offerSentConversationIdRef = useRef<number | null>(null);
  const wsAudioWatchdogRef = useRef<number | null>(null);
  const localVideoElementRef = useRef<HTMLVideoElement | null>(null);
  const remoteVideoElementRef = useRef<HTMLVideoElement | null>(null);
  const localVideoSenderRef = useRef<RTCRtpSender | null>(null);
  const ringtoneRef = useRef<{ stop: () => void } | null>(null);
  const originalTitleRef = useRef<string>('');
  const remoteTypingTimeoutRef = useRef<number | null>(null);
  const localTypingTimeoutRef = useRef<number | null>(null);
  const localTypingActiveRef = useRef(false);
  const pendingSocketRequestsRef = useRef<Map<string, PendingSocketRequest>>(new Map());
  const heartbeatIntervalRef = useRef<number | null>(null);
  const pongTimeoutRef = useRef<number | null>(null);

  const rejectPendingSocketRequests = useCallback((message: string) => {
    pendingSocketRequestsRef.current.forEach((pendingRequest) => {
      window.clearTimeout(pendingRequest.timeoutId);
      pendingRequest.reject(new Error(message));
    });
    pendingSocketRequestsRef.current.clear();
  }, []);

  const clearRemoteTypingIndicator = useCallback(() => {
    if (remoteTypingTimeoutRef.current !== null) {
      window.clearTimeout(remoteTypingTimeoutRef.current);
      remoteTypingTimeoutRef.current = null;
    }
    setRemoteTypingLabel('');
  }, []);

  const clearLocalTypingTimer = useCallback(() => {
    if (localTypingTimeoutRef.current !== null) {
      window.clearTimeout(localTypingTimeoutRef.current);
      localTypingTimeoutRef.current = null;
    }
  }, []);

  const clearSocketHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current !== null) {
      window.clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    if (pongTimeoutRef.current !== null) {
      window.clearTimeout(pongTimeoutRef.current);
      pongTimeoutRef.current = null;
    }
  }, []);

  const acknowledgeSocketHeartbeat = useCallback(() => {
    if (pongTimeoutRef.current !== null) {
      window.clearTimeout(pongTimeoutRef.current);
      pongTimeoutRef.current = null;
    }
  }, []);

  const startSocketHeartbeat = useCallback(() => {
    clearSocketHeartbeat();
    heartbeatIntervalRef.current = window.setInterval(() => {
      const socket = socketRef.current;
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        return;
      }

      acknowledgeSocketHeartbeat();
      pongTimeoutRef.current = window.setTimeout(() => {
        const activeSocket = socketRef.current;
        if (activeSocket && activeSocket.readyState === WebSocket.OPEN) {
          activeSocket.close();
        }
      }, 10000);

      try {
        socket.send(JSON.stringify({ type: 'ping' }));
      } catch {
        acknowledgeSocketHeartbeat();
        socket.close();
      }
    }, 20000);
  }, [acknowledgeSocketHeartbeat, clearSocketHeartbeat]);

  const waitForSocketConnection = useCallback((timeoutMs = 5000) => {
    return new Promise<void>((resolve, reject) => {
      const deadline = Date.now() + timeoutMs;

      const poll = () => {
        const socket = socketRef.current;
        if (socket && socket.readyState === WebSocket.OPEN) {
          resolve();
          return;
        }

        if (Date.now() >= deadline) {
          reject(new Error('WEBSOCKET_UNAVAILABLE'));
          return;
        }

        window.setTimeout(poll, 150);
      };

      poll();
    });
  }, []);

  const forceSocketReconnect = useCallback(() => {
    const socket = socketRef.current;
    if (socket) {
      try {
        socket.close();
      } catch {
        // Ignore close errors during forced reconnect.
      }
    }
  }, []);

  const loadNotificationSummary = useCallback(async () => {
    const summaryResponse = await clientPortalApi.getNotificationSummary();
    const nextSupportOnline = Boolean(
      (summaryResponse.data as ClientPortalNotificationSummary)?.support_online
    );
    supportOnlineRef.current = nextSupportOnline;
    setSupportOnline(nextSupportOnline);
    window.dispatchEvent(new Event('client-portal-notifications-changed'));
    return nextSupportOnline;
  }, []);

  const loadConversation = useCallback(async () => {
    try {
      const [conversationResponse, nextSupportOnline] = await Promise.all([
        clientPortalApi.getChatConversation(),
        loadNotificationSummary(),
      ]);
      const nextConversation = conversationResponse.data as ClientPortalChatConversation;
      conversationStateRef.current = getConversationStateToken(nextConversation);
      supportOnlineRef.current = nextSupportOnline;
      conversationRef.current = nextConversation;
      setConversation(nextConversation);
      setSupportOnline(nextSupportOnline);
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Chargement du chat impossible.', 'error');
    } finally {
      setLoading(false);
    }
  }, [loadNotificationSummary, showToast]);

  const sendMessageViaSocket = useCallback(
    (nextMessageText: string) =>
      new Promise<ClientPortalRealtimeEvent>((resolve, reject) => {
        const socket = socketRef.current;
        if (!socket || socket.readyState !== WebSocket.OPEN) {
          reject(new Error('WEBSOCKET_UNAVAILABLE'));
          return;
        }

        const requestId = `client-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const timeoutId = window.setTimeout(() => {
          pendingSocketRequestsRef.current.delete(requestId);
          reject(new Error('WEBSOCKET_TIMEOUT'));
        }, 12000);

        pendingSocketRequestsRef.current.set(requestId, {
          resolve,
          reject,
          timeoutId,
        });

        try {
          socket.send(
            JSON.stringify({
              type: 'send_message',
              request_id: requestId,
              message_text: nextMessageText,
            })
          );
        } catch (error) {
          window.clearTimeout(timeoutId);
          pendingSocketRequestsRef.current.delete(requestId);
          reject(error instanceof Error ? error : new Error('WEBSOCKET_SEND_FAILED'));
        }
      }),
    []
  );

  const sendTypingSignal = useCallback(
    (conversationId: number | null, isTyping: boolean) => {
      const socket = socketRef.current;
      if (!conversationId || !socket || socket.readyState !== WebSocket.OPEN) {
        if (!isTyping) {
          localTypingActiveRef.current = false;
        }
        return;
      }

      if (localTypingActiveRef.current === isTyping) {
        return;
      }

      try {
        socket.send(
          JSON.stringify({
            type: 'typing',
            conversation_id: conversationId,
            is_typing: isTyping,
          })
        );
        localTypingActiveRef.current = isTyping;
      } catch {
        if (!isTyping) {
          localTypingActiveRef.current = false;
        }
      }
    },
    []
  );

  const scheduleStopTyping = useCallback(
    (conversationId: number | null) => {
      clearLocalTypingTimer();
      localTypingTimeoutRef.current = window.setTimeout(() => {
        sendTypingSignal(conversationId, false);
      }, 1400);
    },
    [clearLocalTypingTimer, sendTypingSignal]
  );

  const applyCallSignal = useCallback((signal: SupportChatCallSignal) => {
    if (signal.call_status === 'idle') {
      setCallSignal(null);
      return;
    }

    setCallSignal(signal);
  }, []);

  const stopRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      ringtoneRef.current.stop();
      ringtoneRef.current = null;
    }
  }, []);

  const startRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      return;
    }
    let audioCtx: AudioContext | null = null;
    try {
      const AudioCtor =
        window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtor) {
        return;
      }
      audioCtx = new AudioCtor();
    } catch {
      return;
    }
    if (!audioCtx) {
      return;
    }
    const ctx = audioCtx;
    let stopped = false;
    const timeouts: number[] = [];

    const playBeep = (frequency: number, duration: number, delay: number) => {
      if (stopped) {
        return;
      }
      try {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        oscillator.type = 'sine';
        oscillator.frequency.value = frequency;
        const start = ctx.currentTime + delay;
        gainNode.gain.setValueAtTime(0, start);
        gainNode.gain.linearRampToValueAtTime(0.15, start + 0.05);
        gainNode.gain.setValueAtTime(0.15, start + duration - 0.05);
        gainNode.gain.linearRampToValueAtTime(0, start + duration);
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        oscillator.start(start);
        oscillator.stop(start + duration);
      } catch {
        // ignore audio errors
      }
    };

    const ringCycle = () => {
      if (stopped) {
        return;
      }
      playBeep(880, 0.4, 0);
      playBeep(660, 0.4, 0.5);
      const id = window.setTimeout(ringCycle, 2000);
      timeouts.push(id);
    };

    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => undefined);
    }
    ringCycle();

    ringtoneRef.current = {
      stop: () => {
        stopped = true;
        timeouts.forEach((id) => window.clearTimeout(id));
        try {
          void ctx.close();
        } catch {
          // ignore close errors
        }
      },
    };
  }, []);

  const sendCallCommandViaSocket = useCallback(
    (
      commandType: 'call_request' | 'call_accept' | 'call_refuse' | 'call_end',
      conversationId: number
    ) =>
      new Promise<ClientPortalRealtimeEvent>((resolve, reject) => {
        const socket = socketRef.current;
        if (!socket || socket.readyState !== WebSocket.OPEN) {
          reject(new Error('WEBSOCKET_UNAVAILABLE'));
          return;
        }

        const requestId = `${commandType}-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2)}`;
        const timeoutId = window.setTimeout(() => {
          pendingSocketRequestsRef.current.delete(requestId);
          reject(new Error('WEBSOCKET_TIMEOUT'));
        }, 12000);

        pendingSocketRequestsRef.current.set(requestId, {
          resolve,
          reject,
          timeoutId,
        });

        try {
          socket.send(
            JSON.stringify({
              type: commandType,
              request_id: requestId,
              conversation_id: conversationId,
            })
          );
        } catch (error) {
          window.clearTimeout(timeoutId);
          pendingSocketRequestsRef.current.delete(requestId);
          reject(error instanceof Error ? error : new Error('WEBSOCKET_SEND_FAILED'));
        }
      }),
    []
  );

  const sendWebRTCSignalViaSocket = useCallback(
    (
      signalType: 'offer' | 'answer' | 'ice_candidate',
      conversationId: number,
      data: ChatWebRTCSignalInput
    ) =>
      new Promise<ClientPortalRealtimeEvent>((resolve, reject) => {
        const socket = socketRef.current;
        if (!socket || socket.readyState !== WebSocket.OPEN) {
          reject(new Error('WEBSOCKET_UNAVAILABLE'));
          return;
        }

        const requestId = `webrtc-${signalType}-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2)}`;
        const timeoutId = window.setTimeout(() => {
          pendingSocketRequestsRef.current.delete(requestId);
          reject(new Error('WEBSOCKET_TIMEOUT'));
        }, 12000);

        pendingSocketRequestsRef.current.set(requestId, {
          resolve,
          reject,
          timeoutId,
        });

        try {
          socket.send(
            JSON.stringify({
              type:
                signalType === 'offer'
                  ? 'webrtc_offer'
                  : signalType === 'answer'
                  ? 'webrtc_answer'
                  : 'webrtc_ice_candidate',
              request_id: requestId,
              conversation_id: conversationId,
              sdp: data.sdp || '',
              candidate: data.candidate || '',
              sdp_mid: data.sdp_mid || '',
              sdp_mline_index:
                typeof data.sdp_mline_index === 'number' ? data.sdp_mline_index : -1,
            })
          );
        } catch (error) {
          window.clearTimeout(timeoutId);
          pendingSocketRequestsRef.current.delete(requestId);
          reject(error instanceof Error ? error : new Error('WEBSOCKET_SEND_FAILED'));
        }
      }),
    []
  );

  const sendWebRTCSignal = useCallback(
    async (
      signalType: 'offer' | 'answer' | 'ice_candidate',
      conversationId: number,
      data: ChatWebRTCSignalInput
    ) => {
      try {
        await waitForSocketConnection();
        await sendWebRTCSignalViaSocket(signalType, conversationId, data);
        return;
      } catch (error: any) {
        const socketMessage = error instanceof Error ? error.message : '';
        if (!isTransientSocketError(socketMessage)) {
          throw error;
        }
      }

      forceSocketReconnect();
      try {
        await clientPortalApi.sendChatWebRTCSignal(signalType, conversationId, data);
      } catch (apiError: any) {
        throw new Error(apiError.response?.data?.error || 'Signal WebRTC impossible.');
      }
    },
    [forceSocketReconnect, sendWebRTCSignalViaSocket, waitForSocketConnection]
  );

  const releaseAudioTransport = useCallback(
    (
      options: {
        stopLocalTracks?: boolean;
        clearActiveConversation?: boolean;
        label?: string;
      } = {}
    ) => {
      const {
        stopLocalTracks = true,
        clearActiveConversation = true,
        label = 'Aucun appel audio actif.',
      } = options;

      const peerConnection = peerConnectionRef.current;
      if (peerConnection) {
        peerConnection.onicecandidate = null;
        peerConnection.ontrack = null;
        peerConnection.onconnectionstatechange = null;
        peerConnection.oniceconnectionstatechange = null;
        try {
          peerConnection.close();
        } catch {
          // Ignore close errors during cleanup.
        }
      }
      peerConnectionRef.current = null;

      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = null;
      }

      if (remoteStreamRef.current) {
        remoteStreamRef.current.getTracks().forEach((track) => track.stop());
        remoteStreamRef.current = null;
      }

      if (stopLocalTracks && localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
        localStreamRef.current = null;
      }

      pendingIceCandidatesRef.current = [];
      offerSentConversationIdRef.current = null;
      localVideoSenderRef.current = null;
      if (clearActiveConversation) {
        activeAudioConversationIdRef.current = null;
      }
      if (localVideoElementRef.current) {
        localVideoElementRef.current.srcObject = null;
      }
      if (remoteVideoElementRef.current) {
        remoteVideoElementRef.current.srcObject = null;
      }
      setVideoEnabled(false);
      setVideoPreparing(false);
      setHasRemoteVideo(false);
      setAudioPreparing(false);
      setAudioMuted(false);
      setAudioStatusLabel(label);
    },
    []
  );

  const flushPendingIceCandidates = useCallback(async (peerConnection: RTCPeerConnection) => {
    if (!peerConnection.remoteDescription || pendingIceCandidatesRef.current.length === 0) {
      return;
    }

    const nextCandidates = [...pendingIceCandidatesRef.current];
    pendingIceCandidatesRef.current = [];

    for (const candidate of nextCandidates) {
      await peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
    }
  }, []);

  const enableLocalVideo = useCallback(
    async (conversationId: number) => {
      if (videoEnabled || videoPreparing) {
        return;
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        showToast('Le navigateur ne prend pas en charge la video WebRTC.', 'error');
        return;
      }
      const peerConnection = peerConnectionRef.current;
      if (!peerConnection || peerConnection.signalingState === 'closed') {
        showToast('Aucun appel audio actif.', 'warning');
        return;
      }

      setVideoPreparing(true);
      try {
        const videoStream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false,
        });
        const videoTrack = videoStream.getVideoTracks()[0];
        if (!videoTrack) {
          throw new Error('Aucune piste video disponible.');
        }

        if (!localStreamRef.current) {
          localStreamRef.current = new MediaStream();
        }
        localStreamRef.current.addTrack(videoTrack);

        if (localVideoElementRef.current) {
          localVideoElementRef.current.srcObject = localStreamRef.current;
          void localVideoElementRef.current.play().catch(() => undefined);
        }

        const sender = peerConnection.addTrack(videoTrack, localStreamRef.current);
        localVideoSenderRef.current = sender;

        const offer = await peerConnection.createOffer();
        await peerConnection.setLocalDescription(offer);
        await sendWebRTCSignal('offer', conversationId, {
          sdp: offer.sdp || '',
        });

        setVideoEnabled(true);
      } catch (error: any) {
        if (error?.name === 'NotAllowedError') {
          showToast('Acces a la camera refuse. Autorisez le navigateur puis recommencez.', 'error');
        } else if (error?.name === 'NotFoundError') {
          showToast('Aucune camera detectee sur cet appareil.', 'error');
        } else {
          showToast(error?.message || 'Activation de la camera impossible.', 'error');
        }
      } finally {
        setVideoPreparing(false);
      }
    },
    [sendWebRTCSignal, showToast, videoEnabled, videoPreparing]
  );

  const disableLocalVideo = useCallback(
    async (conversationId: number) => {
      const sender = localVideoSenderRef.current;
      const peerConnection = peerConnectionRef.current;

      setVideoEnabled(false);

      if (localStreamRef.current) {
        localStreamRef.current.getVideoTracks().forEach((track) => {
          track.stop();
          localStreamRef.current?.removeTrack(track);
        });
      }
      if (localVideoElementRef.current) {
        localVideoElementRef.current.srcObject = null;
      }

      if (sender && peerConnection && peerConnection.signalingState !== 'closed') {
        try {
          peerConnection.removeTrack(sender);
        } catch {
          // Ignore removeTrack failures.
        }
        try {
          const offer = await peerConnection.createOffer();
          await peerConnection.setLocalDescription(offer);
          await sendWebRTCSignal('offer', conversationId, {
            sdp: offer.sdp || '',
          });
        } catch (error: any) {
          showToast(error?.message || 'Renegociation video impossible.', 'error');
        }
      }
      localVideoSenderRef.current = null;
    },
    [sendWebRTCSignal, showToast]
  );

  const toggleVideo = useCallback(() => {
    const conversationId = activeAudioConversationIdRef.current || 0;
    if (!conversationId) {
      return;
    }
    if (videoEnabled) {
      void disableLocalVideo(conversationId);
    } else {
      void enableLocalVideo(conversationId);
    }
  }, [disableLocalVideo, enableLocalVideo, videoEnabled]);

  const getOrCreatePeerConnection = useCallback(
    (conversationId: number) => {
      if (
        activeAudioConversationIdRef.current !== null &&
        activeAudioConversationIdRef.current !== conversationId
      ) {
        throw new Error(
          'Un appel audio est deja actif sur une autre conversation.'
        );
      }

      const currentPeerConnection = peerConnectionRef.current;
      if (currentPeerConnection && currentPeerConnection.signalingState !== 'closed') {
        return currentPeerConnection;
      }

      const peerConnection = new RTCPeerConnection({
        iceServers: buildWebRTCIceServers(),
      });

      activeAudioConversationIdRef.current = conversationId;

      peerConnection.onicecandidate = (event) => {
        if (!event.candidate) {
          return;
        }

        void sendWebRTCSignal('ice_candidate', conversationId, {
          candidate: event.candidate.candidate,
          sdp_mid: event.candidate.sdpMid || '',
          sdp_mline_index:
            typeof event.candidate.sdpMLineIndex === 'number'
              ? event.candidate.sdpMLineIndex
              : -1,
        }).catch((error: Error) => {
          showToast(error.message || 'Envoi du candidat ICE impossible.', 'error');
        });
      };

      peerConnection.ontrack = (event) => {
        if (!remoteStreamRef.current) {
          remoteStreamRef.current = new MediaStream();
        }

        const remoteStream = remoteStreamRef.current;
        if (event.streams && event.streams[0]) {
          event.streams[0].getTracks().forEach((track) => {
            if (!remoteStream.getTracks().some((currentTrack) => currentTrack.id === track.id)) {
              remoteStream.addTrack(track);
            }
          });
        } else if (
          !remoteStream.getTracks().some((currentTrack) => currentTrack.id === event.track.id)
        ) {
          remoteStream.addTrack(event.track);
        }

        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = remoteStream;
          void remoteAudioRef.current.play().catch(() => undefined);
        }

        const remoteHasVideo = remoteStream
          .getVideoTracks()
          .some((track) => track.readyState === 'live');
        setHasRemoteVideo(remoteHasVideo);
        if (remoteHasVideo && remoteVideoElementRef.current) {
          remoteVideoElementRef.current.srcObject = remoteStream;
          void remoteVideoElementRef.current.play().catch(() => undefined);
        }

        event.track.addEventListener('ended', () => {
          const stillHasVideo = remoteStream
            .getVideoTracks()
            .some((track) => track.readyState === 'live');
          setHasRemoteVideo(stillHasVideo);
        });

        setAudioStatusLabel('Flux audio connecte.');
      };

      peerConnection.onconnectionstatechange = () => {
        switch (peerConnection.connectionState) {
          case 'connected':
            setAudioStatusLabel('Flux audio connecte.');
            break;
          case 'connecting':
            setAudioStatusLabel('Connexion audio en cours...');
            break;
          case 'disconnected':
            setAudioStatusLabel('Connexion audio interrompue.');
            break;
          case 'failed':
            setAudioStatusLabel('Connexion audio echouee.');
            break;
          case 'closed':
            setAudioStatusLabel('Appel audio termine.');
            break;
          default:
            setAudioStatusLabel('Negociation audio en cours...');
            break;
        }
      };

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => {
          const alreadyAdded = peerConnection
            .getSenders()
            .some((sender) => sender.track?.id === track.id);
          if (!alreadyAdded) {
            peerConnection.addTrack(track, localStreamRef.current as MediaStream);
          }
        });
      }

      peerConnectionRef.current = peerConnection;
      return peerConnection;
    },
    [sendWebRTCSignal, showToast]
  );

  const ensureLocalAudioReady = useCallback(
    async (conversationId: number) => {
      const existingStream = localStreamRef.current;
      if (existingStream && existingStream.getAudioTracks().some((track) => track.readyState === 'live')) {
        const peerConnection = getOrCreatePeerConnection(conversationId);
        existingStream.getTracks().forEach((track) => {
          const alreadyAdded = peerConnection
            .getSenders()
            .some((sender) => sender.track?.id === track.id);
          if (!alreadyAdded) {
            peerConnection.addTrack(track, existingStream);
          }
        });
        return existingStream;
      }

      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Le navigateur ne prend pas en charge l audio WebRTC.');
      }

      if (
        !window.isSecureContext &&
        !['localhost', '127.0.0.1'].includes(window.location.hostname)
      ) {
        throw new Error(
          'Le micro du navigateur exige HTTPS ou un acces local pour fonctionner.'
        );
      }

      setAudioPreparing(true);
      setAudioStatusLabel('Activation du micro...');
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        });
        localStreamRef.current = stream;
        setAudioMuted(false);

        const peerConnection = getOrCreatePeerConnection(conversationId);
        stream.getTracks().forEach((track) => {
          const alreadyAdded = peerConnection
            .getSenders()
            .some((sender) => sender.track?.id === track.id);
          if (!alreadyAdded) {
            peerConnection.addTrack(track, stream);
          }
        });

        return stream;
      } catch (error: any) {
        if (error?.name === 'NotAllowedError') {
          throw new Error('Acces au micro refuse. Autorisez le navigateur puis recommencez.');
        }
        if (error?.name === 'NotFoundError') {
          throw new Error('Aucun micro detecte sur cet appareil.');
        }
        throw new Error(error?.message || 'Activation du micro impossible.');
      } finally {
        setAudioPreparing(false);
      }
    },
    [getOrCreatePeerConnection]
  );

  const handleIncomingWebRTCSignal = useCallback(
    async (payload: ClientPortalRealtimeEvent) => {
      const conversationId = payload.conversation_id || 0;
      if (!conversationId || conversationId !== conversationRef.current?.id) {
        return;
      }

      if (payload.actor_type === 'client' || !payload.signal_type) {
        return;
      }

      if (callSignalRef.current?.call_status !== 'active') {
        return;
      }

      activeAudioConversationIdRef.current = conversationId;

      try {
        await ensureLocalAudioReady(conversationId);
        const peerConnection = getOrCreatePeerConnection(conversationId);

        if (payload.signal_type === 'offer') {
          setAudioStatusLabel('Offre audio recue. Preparation de la reponse...');
          await peerConnection.setRemoteDescription({
            type: 'offer',
            sdp: payload.sdp || '',
          });
          await flushPendingIceCandidates(peerConnection);
          const answer = await peerConnection.createAnswer();
          await peerConnection.setLocalDescription(answer);
          await sendWebRTCSignal('answer', conversationId, {
            sdp: answer.sdp || '',
          });
          setAudioStatusLabel('Reponse audio envoyee.');
          return;
        }

        if (payload.signal_type === 'answer') {
          if (payload.sdp) {
            await peerConnection.setRemoteDescription({
              type: 'answer',
              sdp: payload.sdp,
            });
            await flushPendingIceCandidates(peerConnection);
            setAudioStatusLabel('Connexion audio en cours...');
          }
          return;
        }

        if (payload.signal_type === 'ice_candidate' && payload.candidate) {
          const iceCandidate: RTCIceCandidateInit = {
            candidate: payload.candidate,
            sdpMid: payload.sdp_mid || undefined,
            sdpMLineIndex:
              typeof payload.sdp_mline_index === 'number'
                ? payload.sdp_mline_index
                : undefined,
          };

          if (peerConnection.remoteDescription) {
            await peerConnection.addIceCandidate(new RTCIceCandidate(iceCandidate));
          } else {
            pendingIceCandidatesRef.current.push(iceCandidate);
          }
        }
      } catch (error: any) {
        showToast(error.message || 'Traitement audio WebRTC impossible.', 'error');
      }
    },
    [
      ensureLocalAudioReady,
      flushPendingIceCandidates,
      getOrCreatePeerConnection,
      sendWebRTCSignal,
      showToast,
    ]
  );

  const startAudioOfferIfNeeded = useCallback(
    async (conversationId: number) => {
      if (offerSentConversationIdRef.current === conversationId) {
        return;
      }

      await ensureLocalAudioReady(conversationId);
      const peerConnection = getOrCreatePeerConnection(conversationId);
      setAudioStatusLabel('Preparation de l offre audio...');
      const offer = await peerConnection.createOffer({
        offerToReceiveAudio: true,
      });
      await peerConnection.setLocalDescription(offer);
      offerSentConversationIdRef.current = conversationId;
      await sendWebRTCSignal('offer', conversationId, {
        sdp: offer.sdp || '',
      });
      setAudioStatusLabel('Offre audio envoyee.');
    },
    [ensureLocalAudioReady, getOrCreatePeerConnection, sendWebRTCSignal]
  );

  const toggleAudioMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) {
      return;
    }

    const nextMuted = !audioMuted;
    stream.getAudioTracks().forEach((track) => {
      track.enabled = !nextMuted;
    });
    setAudioMuted(nextMuted);
  }, [audioMuted]);

  useEffect(() => {
    if (user?.must_change_password) {
      setLoading(false);
      return;
    }

    void loadConversation();
  }, [loadConversation, user?.must_change_password]);

  useEffect(() => {
    conversationRef.current = conversation;
  }, [conversation]);

  useEffect(() => {
    callSignalRef.current = callSignal;
  }, [callSignal]);

  useEffect(() => {
    if (user?.must_change_password) {
      return undefined;
    }

    let cancelled = false;
    let reconnectTimer: number | null = null;
    let socket: WebSocket | null = null;

    const connectSocket = () => {
      // Pré-chauffe les credentials TURN pendant que le chat se connecte :
      // le premier appel de la session part avec un relais déjà prêt.
      void refreshDynamicIceServers();
      const socketUrl = realtimeApi.buildClientChatSocketUrl();
      if (!socketUrl || cancelled) {
        return;
      }

      try {
        socket = new WebSocket(socketUrl);
        socketRef.current = socket;
      } catch {
        socketRef.current = null;
        reconnectTimer = window.setTimeout(connectSocket, 2000);
        return;
      }

      socket.onopen = () => {
        socketActiveRef.current = true;
        setIsSocketConnected(true);
        acknowledgeSocketHeartbeat();
        startSocketHeartbeat();
        if (wsAudioWatchdogRef.current !== null) {
          window.clearTimeout(wsAudioWatchdogRef.current);
          wsAudioWatchdogRef.current = null;
        }
      };

      socket.onmessage = (event) => {
        let payload: ClientPortalRealtimeEvent | null = null;
        try {
          payload = JSON.parse(event.data) as ClientPortalRealtimeEvent;
        } catch {
          return;
        }

        if (
          ((payload.type && payload.type.endsWith('_result')) || payload.type === 'error') &&
          payload.request_id
        ) {
          const pendingRequest = pendingSocketRequestsRef.current.get(payload.request_id);
          if (pendingRequest) {
            window.clearTimeout(pendingRequest.timeoutId);
            pendingSocketRequestsRef.current.delete(payload.request_id);
            if (payload.success === false || payload.error) {
              pendingRequest.reject(new Error(payload.error || 'Envoi du message impossible.'));
            } else {
              pendingRequest.resolve(payload);
            }
          }
          return;
        }

        if (!payload?.type) {
          return;
        }

        if (payload.type === 'connected' || payload.type === 'pong') {
          acknowledgeSocketHeartbeat();
          return;
        }

        if (payload.type === 'call_signal') {
          applyCallSignal(payload as SupportChatCallSignal);
          return;
        }

        if (payload.type === 'webrtc_signal') {
          void handleIncomingWebRTCSignal(payload);
          return;
        }

        if (
          payload.type === 'typing' &&
          payload.sender_type === 'staff' &&
          payload.conversation_id === conversationRef.current?.id
        ) {
          if (!payload.is_typing) {
            clearRemoteTypingIndicator();
            return;
          }

          const actorName =
            payload.actor_name ||
            conversationRef.current?.assigned_staff_name ||
            'Equipe support';
          setRemoteTypingLabel(`${actorName} ecrit...`);
          if (remoteTypingTimeoutRef.current !== null) {
            window.clearTimeout(remoteTypingTimeoutRef.current);
          }
          remoteTypingTimeoutRef.current = window.setTimeout(() => {
            setRemoteTypingLabel('');
            remoteTypingTimeoutRef.current = null;
          }, 2600);
          return;
        }

        if (payload.type === 'chat_changed') {
          clearRemoteTypingIndicator();
          void loadConversation();
          return;
        }

        if (payload.type === 'support_presence' && typeof payload.support_online === 'boolean') {
          supportOnlineRef.current = payload.support_online;
          setSupportOnline(payload.support_online);
        }
      };

      socket.onerror = () => {
        socket?.close();
      };

      socket.onclose = () => {
        socketRef.current = null;
        socketActiveRef.current = false;
        clearSocketHeartbeat();
        clearRemoteTypingIndicator();
        clearLocalTypingTimer();
        localTypingActiveRef.current = false;
        setProcessingCallAction(null);
        setIsSocketConnected(false);
        rejectPendingSocketRequests('WEBSOCKET_CLOSED');
        if (wsAudioWatchdogRef.current !== null) {
          window.clearTimeout(wsAudioWatchdogRef.current);
        }
        wsAudioWatchdogRef.current = window.setTimeout(() => {
          wsAudioWatchdogRef.current = null;
          if (activeAudioConversationIdRef.current !== null) {
            releaseAudioTransport({ label: 'Connexion perdue, appel termine.' });
          }
        }, 15000);
        if (!cancelled) {
          reconnectTimer = window.setTimeout(connectSocket, 2000);
        }
      };
    };

    connectSocket();

    return () => {
      cancelled = true;
      socketRef.current = null;
      socketActiveRef.current = false;
      clearSocketHeartbeat();
      setIsSocketConnected(false);
      rejectPendingSocketRequests('WEBSOCKET_CLOSED');
      if (wsAudioWatchdogRef.current !== null) {
        window.clearTimeout(wsAudioWatchdogRef.current);
        wsAudioWatchdogRef.current = null;
      }
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      socket?.close();
    };
  }, [
    acknowledgeSocketHeartbeat,
    applyCallSignal,
    clearLocalTypingTimer,
    clearRemoteTypingIndicator,
    clearSocketHeartbeat,
    handleIncomingWebRTCSignal,
    loadConversation,
    rejectPendingSocketRequests,
    releaseAudioTransport,
    startSocketHeartbeat,
    user?.must_change_password,
  ]);

  useEffect(() => {
    return () => {
      clearLocalTypingTimer();
      sendTypingSignal(conversationRef.current?.id || null, false);
      clearRemoteTypingIndicator();
    };
  }, [clearLocalTypingTimer, clearRemoteTypingIndicator, sendTypingSignal]);

  // Sonnerie d'appel entrant : detecte un call_signal 'ringing' avec initiator='staff'
  useEffect(() => {
    const hasIncomingRingingCall = Boolean(
      callSignal &&
        callSignal.call_status === 'ringing' &&
        callSignal.initiator_type === 'staff'
    );

    if (hasIncomingRingingCall) {
      if (!originalTitleRef.current) {
        originalTitleRef.current = document.title;
      }
      startRingtone();
      document.title = `Appel entrant - ${originalTitleRef.current || 'Winalco'}`;
    } else {
      stopRingtone();
      if (originalTitleRef.current) {
        document.title = originalTitleRef.current;
      }
    }

    return () => {
      stopRingtone();
      if (originalTitleRef.current) {
        document.title = originalTitleRef.current;
      }
    };
  }, [callSignal, startRingtone, stopRingtone]);

  useEffect(() => {
    if (user?.must_change_password) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      void loadNotificationSummary();
    }, 60000);

    return () => {
      clearInterval(intervalId);
    };
  }, [loadNotificationSummary, user?.must_change_password]);

  useEffect(() => {
    if (user?.must_change_password) {
      return undefined;
    }

    let cancelled = false;

    const waitFor = (delayMs: number) =>
      new Promise<void>((resolve) => {
        window.setTimeout(resolve, delayMs);
      });

    const runRealtimeLoop = async () => {
      while (!cancelled) {
        if (document.visibilityState === 'hidden' || sending || socketActiveRef.current) {
          await waitFor(1000);
          continue;
        }

        try {
          const response = await clientPortalApi.waitForChatUpdates(
            conversationStateRef.current || undefined,
            supportOnlineRef.current
          );
          if (cancelled) {
            return;
          }

          const signal = response.data as ClientPortalChatUpdateSignal;
          supportOnlineRef.current = Boolean(signal.support_online);
          setSupportOnline(Boolean(signal.support_online));

          if (signal.conversation_updated_at) {
            conversationStateRef.current = signal.conversation_updated_at;
          }

          if (signal.conversation_changed) {
            await loadConversation();
          }
        } catch {
          if (cancelled) {
            return;
          }
          await waitFor(2000);
        }
      }
    };

    void runRealtimeLoop();

    return () => {
      cancelled = true;
    };
  }, [loadConversation, sending, user?.must_change_password]);

  useEffect(() => {
    const activeConversationId = conversationRef.current?.id || 0;
    if (!callSignal || callSignal.call_status === 'idle') {
      releaseAudioTransport();
      return;
    }

    if (!activeConversationId || callSignal.conversation_id !== activeConversationId) {
      return;
    }

    activeAudioConversationIdRef.current = activeConversationId;

    if (callSignal.call_status === 'ringing') {
      offerSentConversationIdRef.current = null;
      setAudioStatusLabel(
        callSignal.initiator_type === 'staff'
          ? 'Invitation audio recue. En attente de votre reponse.'
          : 'Demande audio envoyee. En attente de validation.'
      );
      return;
    }

    if (callSignal.initiator_type === 'client') {
      void startAudioOfferIfNeeded(activeConversationId).catch((error: Error) => {
        showToast(error.message || 'Preparation de l appel audio impossible.', 'error');
      });
      return;
    }

    void ensureLocalAudioReady(activeConversationId)
      .then(() => {
        setAudioStatusLabel('Appel accepte. Reception du flux audio...');
      })
      .catch((error: Error) => {
        showToast(error.message || 'Activation du micro impossible.', 'error');
      });
  }, [
    callSignal,
    ensureLocalAudioReady,
    releaseAudioTransport,
    showToast,
    startAudioOfferIfNeeded,
  ]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation?.messages]);

  useEffect(() => {
    return () => {
      releaseAudioTransport();
    };
  }, [releaseAudioTransport]);

  const conversationIsClosed = isConversationClosed(conversation?.status);
  const hasAudioSignal = Boolean(
    callSignal && (callSignal.call_status === 'ringing' || callSignal.call_status === 'active')
  );
  const isIncomingAudioRequest = Boolean(
    callSignal?.call_status === 'ringing' && callSignal.initiator_type === 'staff'
  );
  const isOutgoingAudioRequest = Boolean(
    callSignal?.call_status === 'ringing' && callSignal.initiator_type === 'client'
  );
  const isAudioSessionActive = callSignal?.call_status === 'active';
  const canRequestAudioCall = Boolean(
    conversation?.id && !hasAudioSignal && !sending
  );
  const canAcceptAudioCall = Boolean(
    conversation?.id && isIncomingAudioRequest && !sending
  );
  const canRefuseAudioCall = Boolean(
    conversation?.id && isIncomingAudioRequest && !sending
  );
  const canEndAudioCall = Boolean(conversation?.id && hasAudioSignal);

  const handleChatAttachmentSelection = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';

    if (selectedFiles.length === 0) {
      return;
    }

    const remainingSlots = MAX_CHAT_ATTACHMENTS - chatAttachments.length;
    if (remainingSlots <= 0) {
      showToast(`Maximum ${MAX_CHAT_ATTACHMENTS} pieces jointes par message.`, 'warning');
      return;
    }

    const filesToRead = selectedFiles.slice(0, remainingSlots);
    const skippedReasons: string[] = [];

    if (selectedFiles.length > filesToRead.length) {
      skippedReasons.push(`maximum ${MAX_CHAT_ATTACHMENTS} fichiers atteint`);
    }

    const validFiles = filesToRead.filter((file) => {
      const extension = getFileExtension(file.name);
      if (!CHAT_ATTACHMENT_EXTENSIONS.includes(extension)) {
        skippedReasons.push(`${file.name}: format non autorise`);
        return false;
      }

      if (file.size > MAX_CHAT_ATTACHMENT_BYTES) {
        skippedReasons.push(`${file.name}: fichier trop volumineux`);
        return false;
      }

      return true;
    });

    try {
      const encodedAttachments = await Promise.all(
        validFiles.map(async (file): Promise<ChatMessageAttachmentInput> => ({
          file_name: file.name,
          content_type: file.type || 'application/octet-stream',
          file_size: file.size,
          base64_content: await readFileAsBase64(file),
        }))
      );

      setChatAttachments((prev) => [...prev, ...encodedAttachments]);

      if (skippedReasons.length > 0) {
        showToast(skippedReasons.join(' | '), 'warning');
      }
    } catch (error: any) {
      showToast(error.message || 'Lecture des pieces jointes impossible.', 'error');
    }
  };

  const handleRemoveChatAttachment = (indexToRemove: number) => {
    setChatAttachments((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleDownloadChatAttachment = async (
    messageId: number,
    attachment: SupportChatAttachment
  ) => {
    setDownloadingChatAttachmentId(attachment.id);
    try {
      const response = await clientPortalApi.downloadChatAttachment(messageId, attachment.id);
      const blob = new Blob([response.data], {
        type: attachment.content_type || 'application/octet-stream',
      });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = attachment.file_name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Telechargement de la piece jointe impossible.', 'error');
    } finally {
      setDownloadingChatAttachmentId(null);
    }
  };

  const handleSendMessage = async () => {
    const trimmedMessage = messageText.trim();
    if (!trimmedMessage && chatAttachments.length === 0) {
      showToast('Le message du chat ou au moins une piece jointe est obligatoire.', 'warning');
      return;
    }

    setSending(true);
    try {
      sendTypingSignal(conversationRef.current?.id || null, false);
      clearLocalTypingTimer();
      if (chatAttachments.length > 0) {
        await clientPortalApi.sendChatMessage(trimmedMessage, chatAttachments);
      } else if (socketRef.current?.readyState === WebSocket.OPEN) {
        try {
          await sendMessageViaSocket(trimmedMessage);
        } catch (socketError: any) {
          const socketMessage = socketError instanceof Error ? socketError.message : '';
          if (
            socketMessage === 'WEBSOCKET_UNAVAILABLE' ||
            socketMessage === 'WEBSOCKET_TIMEOUT' ||
            socketMessage === 'WEBSOCKET_CLOSED' ||
            socketMessage === 'WEBSOCKET_SEND_FAILED'
          ) {
            await clientPortalApi.sendChatMessage(trimmedMessage);
          } else {
            throw socketError;
          }
        }
      } else {
        await clientPortalApi.sendChatMessage(trimmedMessage);
      }
      setMessageText('');
      setChatAttachments([]);
      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = '';
      }
      await loadConversation();
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Envoi du message impossible.', 'error');
    } finally {
      setSending(false);
    }
  };

  const runCallCommand = useCallback(
    async (
      commandType: 'call_request' | 'call_accept' | 'call_refuse' | 'call_end',
      actionKey: 'request' | 'accept' | 'refuse' | 'end',
      successMessage: string
    ) => {
      const conversationId = conversationRef.current?.id;
      if (!conversationId) {
        return;
      }

      setProcessingCallAction(actionKey);
      try {
        await waitForSocketConnection();
        await sendCallCommandViaSocket(commandType, conversationId);
        showToast(successMessage, 'success');
      } catch (error: any) {
        const socketMessage = error instanceof Error ? error.message : '';
        if (isTransientSocketError(socketMessage)) {
          forceSocketReconnect();
          try {
            await waitForSocketConnection();
            await sendCallCommandViaSocket(commandType, conversationId);
            showToast(successMessage, 'success');
          } catch (retryError: any) {
            const retryMessage =
              retryError instanceof Error ? retryError.message : '';
            if (isTransientSocketError(retryMessage)) {
              try {
                await clientPortalApi.sendChatCallCommand(actionKey, conversationId);
                showToast(successMessage, 'success');
              } catch (apiError: any) {
                showToast(
                  apiError.response?.data?.error || 'Signalisation audio indisponible pour le moment.',
                  'error'
                );
              }
            } else {
              showToast(retryMessage || 'Commande audio impossible.', 'error');
            }
          }
        } else {
          showToast(socketMessage || 'Commande audio impossible.', 'error');
        }
      } finally {
        setProcessingCallAction(null);
      }
    },
    [forceSocketReconnect, sendCallCommandViaSocket, showToast, waitForSocketConnection]
  );

  const handleRequestAudioCall = async () => {
    const conversationId = conversationRef.current?.id;
    if (!conversationId) {
      return;
    }

    try {
      await ensureLocalAudioReady(conversationId);
    } catch (error: any) {
      showToast(error.message || 'Activation du micro impossible.', 'error');
      return;
    }

    await runCallCommand('call_request', 'request', 'Demande d appel audio envoyee.');
  };

  const handleAcceptAudioCall = async () => {
    const conversationId = conversationRef.current?.id;
    if (!conversationId) {
      return;
    }

    try {
      await ensureLocalAudioReady(conversationId);
    } catch (error: any) {
      showToast(error.message || 'Activation du micro impossible.', 'error');
      return;
    }

    await runCallCommand('call_accept', 'accept', 'Demande d appel audio acceptee.');
  };

  const handleRefuseAudioCall = async () => {
    await runCallCommand('call_refuse', 'refuse', 'Demande d appel audio refusee.');
    releaseAudioTransport();
  };

  const handleEndAudioCall = async () => {
    await runCallCommand('call_end', 'end', 'Session audio terminee.');
    releaseAudioTransport();
  };

  const handleMessageTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextValue = event.target.value;
    setMessageText(nextValue);

    const conversationId = conversationRef.current?.id || null;
    if (!nextValue.trim()) {
      clearLocalTypingTimer();
      sendTypingSignal(conversationId, false);
      return;
    }

    sendTypingSignal(conversationId, true);
    scheduleStopTyping(conversationId);
  };

  const handleMessageComposerBlur = () => {
    clearLocalTypingTimer();
    sendTypingSignal(conversationRef.current?.id || null, false);
  };

  if (user?.must_change_password) {
    return <Navigate to="/client/profile" replace />;
  }

  if (loading) {
    return <div className="loading">Chargement du chat support...</div>;
  }

  return (
    <div className="client-portal-stack">
      <section className="client-portal-card client-portal-chat-card">
        <div className="client-portal-card-header">
          <div>
            <h2>Chat support</h2>
            <p>
              Echangez rapidement avec l equipe support. Cette conversation pourra servir
              de base a l audio et a la video plus tard.
            </p>
          </div>
          <div className="client-portal-chat-meta">
            <strong>
              {conversation?.assigned_staff_name
                ? `Pris en charge par ${conversation.assigned_staff_name}`
                : 'En attente de prise en charge'}
            </strong>
            <span
              className={`client-portal-chat-status-badge ${getChatStatusClassName(
                conversation?.status
              )}`}
            >
              {conversation?.status || 'Ouvert'}
            </span>
            <span
              className={`client-portal-support-presence ${
                supportOnline ? 'online' : 'offline'
              }`}
            >
              <span className="client-portal-support-presence-dot" />
              {supportOnline ? 'Equipe support en ligne' : 'Equipe support hors ligne'}
            </span>
            <span>{isSocketConnected ? 'WebSocket actif' : 'Canal de secours actif'}</span>
            <span>
              Derniere activite {formatDateTime(conversation?.last_message_at || conversation?.updated_at)}
            </span>
            {callSignal && (
              <span className={`client-portal-audio-pill ${getAudioSignalClassName(callSignal)}`}>
                {isAudioSessionActive
                  ? 'Signal audio actif'
                  : isIncomingAudioRequest
                  ? 'Appel entrant'
                  : 'Appel en attente'}
              </span>
            )}
          </div>
        </div>

        {conversationIsClosed && (
          <div className="client-portal-chat-banner">
            Chat cloture. Votre prochaine reponse rouvrira automatiquement la conversation.
          </div>
        )}

        {callSignal && (
          <div className={`client-portal-chat-call-banner ${getAudioSignalClassName(callSignal)}`}>
            <strong>
              {isIncomingAudioRequest
                ? `${callSignal.initiator_name || 'Le support'} demande un appel audio.`
                : isOutgoingAudioRequest
                ? 'Demande d appel audio envoyee au support.'
                : 'Appel audio accepte.'}
            </strong>
            <span>
              {isAudioSessionActive
                ? audioStatusLabel
                : audioPreparing
                ? 'Activation du micro en cours...'
                : 'Le micro sera ouvert des validation de l appel.'}
            </span>
          </div>
        )}

        <div className="client-portal-chat-audio-actions">
          {canRequestAudioCall && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleRequestAudioCall}
              disabled={processingCallAction !== null || audioPreparing}
            >
              {processingCallAction === 'request'
                ? 'Demande audio...'
                : audioPreparing
                ? 'Preparation micro...'
                : 'Demander appel audio'}
            </button>
          )}
          {canAcceptAudioCall && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAcceptAudioCall}
              disabled={processingCallAction !== null || audioPreparing}
            >
              {processingCallAction === 'accept'
                ? 'Validation...'
                : audioPreparing
                ? 'Preparation micro...'
                : 'Accepter appel'}
            </button>
          )}
          {canRefuseAudioCall && (
            <button
              type="button"
              className="btn btn-warning"
              onClick={handleRefuseAudioCall}
              disabled={processingCallAction !== null}
            >
              {processingCallAction === 'refuse' ? 'Refus...' : 'Refuser appel'}
            </button>
          )}
          {canEndAudioCall && (
            <button
              type="button"
              className="btn btn-warning"
              onClick={handleEndAudioCall}
              disabled={processingCallAction !== null}
            >
              {processingCallAction === 'end' ? 'Cloture audio...' : 'Terminer appel'}
            </button>
          )}
          {isAudioSessionActive && localStreamRef.current && (
            <button type="button" className="btn btn-secondary" onClick={toggleAudioMute}>
              {audioMuted ? 'Activer micro' : 'Couper micro'}
            </button>
          )}
          {isAudioSessionActive && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={toggleVideo}
              disabled={videoPreparing}
            >
              {videoPreparing
                ? 'Activation camera...'
                : videoEnabled
                ? 'Couper camera'
                : 'Activer camera'}
            </button>
          )}
        </div>
        {(callSignal || activeAudioConversationIdRef.current) && (
          <div className="client-portal-chat-banner">{audioStatusLabel}</div>
        )}
        <audio ref={remoteAudioRef} autoPlay playsInline hidden />

        {(videoEnabled || hasRemoteVideo) && (
          <div
            style={{
              position: 'fixed',
              top: 20,
              right: 20,
              width: 320,
              zIndex: 1100,
              background: '#000',
              borderRadius: 10,
              overflow: 'hidden',
              boxShadow: '0 12px 30px rgba(0, 0, 0, 0.35)',
            }}
          >
            <video
              ref={remoteVideoElementRef}
              autoPlay
              playsInline
              style={{
                width: '100%',
                display: hasRemoteVideo ? 'block' : 'none',
                background: '#000',
              }}
            />
            {!hasRemoteVideo && (
              <div
                style={{
                  padding: '60px 20px',
                  color: '#fff',
                  textAlign: 'center',
                  fontSize: 13,
                }}
              >
                En attente de la video distante...
              </div>
            )}
            {videoEnabled && (
              <video
                ref={localVideoElementRef}
                autoPlay
                playsInline
                muted
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  width: 100,
                  height: 75,
                  background: '#000',
                  borderRadius: 4,
                  border: '2px solid #fff',
                  objectFit: 'cover',
                }}
              />
            )}
          </div>
        )}

        {!!conversation?.call_logs?.length && (
          <div className="client-portal-chat-call-history">
            <div className="client-portal-chat-call-history-header">
              <strong>Historique appels</strong>
              <span>{conversation.call_logs.length} evenement(s)</span>
            </div>
            <div className="client-portal-chat-call-history-list">
              {conversation.call_logs.map((callLog) => (
                <div key={callLog.id} className="client-portal-chat-call-log-item">
                  <div className="client-portal-chat-call-log-top">
                    <span
                      className={`client-portal-chat-call-log-badge ${getCallLogOutcomeClassName(
                        callLog
                      )}`}
                    >
                      {getCallLogOutcomeLabel(callLog)}
                    </span>
                    <span>{formatDateTime(callLog.requested_at)}</span>
                  </div>
                  <div className="client-portal-chat-call-log-meta">
                    <span>
                      {callLog.initiator_type === 'client'
                        ? 'Demande lancee depuis votre espace'
                        : `Demande lancee par ${callLog.initiator_name || 'le support'}`}
                    </span>
                    {callLog.assigned_staff_name && (
                      <span>Staff: {callLog.assigned_staff_name}</span>
                    )}
                    {Number(callLog.duration_seconds || 0) > 0 && (
                      <span>Duree: {formatDurationSeconds(callLog.duration_seconds)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="client-portal-chat-thread">
          {(conversation?.messages || []).length === 0 ? (
            <div className="client-portal-empty">
              Aucun message pour le moment. Envoyez votre premier message.
            </div>
          ) : (
            (conversation?.messages || []).map((message) => (
              <div
                key={message.id}
                className={`client-portal-chat-row ${
                  message.sender_type === 'client' ? 'own' : 'other'
                }`}
              >
                <div className="client-portal-chat-bubble">
                  <div className="client-portal-thread-meta">
                    <strong>
                      {message.sender_type === 'client'
                        ? 'Vous'
                        : message.staff_name || 'Equipe support'}
                    </strong>
                    <span>{formatDateTime(message.created_at)}</span>
                  </div>
                  {hasMessageText(message) && (
                    <div className="client-portal-ticket-message">{message.message_text}</div>
                  )}
                  {message.attachments && message.attachments.length > 0 && (
                    <div className="client-portal-chat-attachments">
                      {message.attachments.map((attachment) => (
                        <button
                          key={attachment.id}
                          type="button"
                          className="client-portal-chat-attachment"
                          onClick={() => handleDownloadChatAttachment(message.id, attachment)}
                          disabled={downloadingChatAttachmentId === attachment.id}
                        >
                          <span>{attachment.file_name}</span>
                          <small>
                            {downloadingChatAttachmentId === attachment.id
                              ? 'Telechargement...'
                              : formatFileSize(attachment.file_size)}
                          </small>
                        </button>
                      ))}
                    </div>
                  )}
                  {message.sender_type === 'client' && (
                    <div
                      className={`client-portal-chat-status ${getMessageStatusClassName(message)}`}
                    >
                      {getMessageStatusLabel(message)}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
          {remoteTypingLabel && (
            <div className="client-portal-chat-typing-row">
              <div className="client-portal-chat-typing-indicator">
                <span className="client-portal-chat-typing-dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
                <span>{remoteTypingLabel}</span>
              </div>
            </div>
          )}
          <div ref={threadEndRef} />
        </div>

        <div className="client-portal-chat-composer">
          <textarea
            rows={4}
            value={messageText}
            onChange={handleMessageTextChange}
            onBlur={handleMessageComposerBlur}
            placeholder="Saisissez votre message pour le support."
            disabled={sending}
          />

          <input
            ref={attachmentInputRef}
            type="file"
            multiple
            accept={CHAT_ATTACHMENT_ACCEPT}
            className="client-portal-chat-hidden-input"
            onChange={handleChatAttachmentSelection}
          />

          {chatAttachments.length > 0 && (
            <div className="client-portal-chat-selected">
              {chatAttachments.map((attachment, index) => (
                <div key={`${attachment.file_name}-${index}`} className="client-portal-chat-selected-item">
                  <div>
                    <strong>{attachment.file_name}</strong>
                    <small>{formatFileSize(attachment.file_size)}</small>
                  </div>
                  <button
                    type="button"
                    className="btn-icon client-portal-chat-remove"
                    onClick={() => handleRemoveChatAttachment(index)}
                    disabled={sending}
                    title="Retirer la piece jointe"
                    aria-label={`Retirer ${attachment.file_name}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="client-portal-form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => attachmentInputRef.current?.click()}
              disabled={sending || chatAttachments.length >= MAX_CHAT_ATTACHMENTS}
            >
              Ajouter une piece jointe
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSendMessage}
              disabled={sending || (!messageText.trim() && chatAttachments.length === 0)}
            >
              {sending ? 'Envoi...' : 'Envoyer'}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ClientPortalChat;
